import React, { useEffect, useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { useToast } from "../hooks/use-toast";
import { fetchMatters, createMatter, updateMatter, deleteMatter, mergeMatters, fetchTags, createTag, updateTag, deleteTag } from "../lib/api";
import { Trash2, Merge } from "lucide-react";

export default function ManagePage(){
  const { toast } = useToast();
  const [matters, setMatters] = useState([]);
  const [taskCodes, setTaskCodes] = useState([]);

  const [newMatter, setNewMatter] = useState({ name: "", color: "" });
  const [newTaskCodeName, setNewTaskCodeName] = useState("");

  // Merge dialog state
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeSource, setMergeSource] = useState("");
  const [mergeTarget, setMergeTarget] = useState("");
  const [merging, setMerging] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [p, t] = await Promise.all([fetchMatters(), fetchTags()]);
        setMatters(p); setTaskCodes(t);
      } catch (e) { console.error(e); }
    })();
  }, []);

  // Matters
  async function addMatter(e){
    e.preventDefault();
    const payload = { name: newMatter.name.trim(), color: newMatter.color || undefined };
    if(!payload.name) return;
    const p = await createMatter(payload);
    setMatters([p, ...matters]);
    setNewMatter({ name: "", color: "" });
  }
  async function saveMatter(id, patch){ const p = await updateMatter(id, patch); setMatters(matters.map(x => x.id===id ? p : x)); }
  async function removeMatter(id){ await deleteMatter(id); setMatters(matters.filter(x => x.id!==id)); }

  function openMergeDialog(){
    setMergeSource("");
    setMergeTarget("");
    setMergeOpen(true);
  }

  async function confirmMerge(){
    if (!mergeSource || !mergeTarget) {
      toast({ title: "Select both matters", description: "Pick the source and the target matter to merge." });
      return;
    }
    if (mergeSource === mergeTarget) {
      toast({ title: "Pick different matters", description: "Source and target must be different." });
      return;
    }
    const srcName = matters.find(m => m.id === mergeSource)?.name || "source";
    const tgtName = matters.find(m => m.id === mergeTarget)?.name || "target";
    const ok = window.confirm(`Merge \"${srcName}\" into \"${tgtName}\"?\n\nAll entries from \"${srcName}\" will be reassigned to \"${tgtName}\", and \"${srcName}\" will be deleted. This cannot be undone.`);
    if (!ok) return;
    setMerging(true);
    try {
      const res = await mergeMatters(mergeSource, mergeTarget);
      setMatters(matters.filter(m => m.id !== mergeSource));
      toast({ title: "Matters merged", description: `${res.entriesMoved} entr${res.entriesMoved === 1 ? 'y' : 'ies'} moved into \"${tgtName}\".` });
      setMergeOpen(false);
    } catch (e) {
      console.error(e);
      toast({ title: "Merge failed", description: e?.response?.data?.detail || "Please try again." });
    } finally {
      setMerging(false);
    }
  }

  // Task Codes
  async function addTaskCode(e){ e.preventDefault(); if(!newTaskCodeName.trim()) return; const t = await createTag({ name: newTaskCodeName.trim() }); setTaskCodes([t, ...taskCodes]); setNewTaskCodeName(""); }
  async function saveTaskCode(id, name){ const t = await updateTag(id, { name }); setTaskCodes(taskCodes.map(x => x.id===id ? t : x)); }
  async function removeTaskCode(id){ await deleteTag(id); setTaskCodes(taskCodes.filter(x => x.id!==id)); }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <h1 className="text-xl font-semibold tracking-tight mb-4">Manage</h1>
      <Tabs defaultValue="matters">
        <TabsList>
          <TabsTrigger value="matters">Matters</TabsTrigger>
          <TabsTrigger value="taskcodes">Task codes</TabsTrigger>
        </TabsList>

        <TabsContent value="matters" className="mt-4">
          <form onSubmit={addMatter} className="flex flex-wrap items-center gap-3 mb-4">
            <Input value={newMatter.name} onChange={(e)=>setNewMatter(v=>({ ...v, name: e.target.value }))} placeholder="Matter name" className="w-64" data-testid="new-matter-name-input"/>
            <Input value={newMatter.color} onChange={(e)=>setNewMatter(v=>({ ...v, color: e.target.value }))} placeholder="#AABBCC (optional)" className="w-48" data-testid="new-matter-color-input"/>
            <Button type="submit" data-testid="add-matter-btn">Add matter</Button>
            <Button type="button" variant="outline" className="gap-2 ml-auto" onClick={openMergeDialog} data-testid="open-merge-matters-btn">
              <Merge className="size-4"/> Merge matters
            </Button>
          </form>

          <div className="rounded border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Color</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {matters.map(p => (
                  <TableRow key={p.id}>
                    <TableCell>
                      <InlineText value={p.name} onChange={(v)=>saveMatter(p.id,{ name:v })} placeholder="Matter name"/>
                    </TableCell>
                    <TableCell>
                      <InlineText value={p.color || ""} onChange={(v)=>saveMatter(p.id,{ color:v || null })} placeholder="#AABBCC"/>
                    </TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={()=>removeMatter(p.id)}><Trash2 className="size-4"/></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Dialog open={mergeOpen} onOpenChange={setMergeOpen}>
            <DialogContent data-testid="merge-matters-dialog">
              <DialogHeader>
                <DialogTitle>Merge matters</DialogTitle>
                <DialogDescription>
                  Move every time entry from the source matter into the target matter, then delete the source. This cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Source matter (will be deleted)</div>
                  <Select value={mergeSource} onValueChange={setMergeSource}>
                    <SelectTrigger data-testid="merge-source-select"><SelectValue placeholder="Select source matter"/></SelectTrigger>
                    <SelectContent>
                      {matters.map(m => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground mb-1">Target matter (entries moved here)</div>
                  <Select value={mergeTarget} onValueChange={setMergeTarget}>
                    <SelectTrigger data-testid="merge-target-select"><SelectValue placeholder="Select target matter"/></SelectTrigger>
                    <SelectContent>
                      {matters.filter(m => m.id !== mergeSource).map(m => (<SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={()=>setMergeOpen(false)} disabled={merging} data-testid="merge-cancel-btn">Cancel</Button>
                <Button onClick={confirmMerge} disabled={merging || !mergeSource || !mergeTarget} data-testid="merge-confirm-btn">
                  {merging ? "Merging..." : "Merge"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="taskcodes" className="mt-4">
          <form onSubmit={addTaskCode} className="flex items-center gap-3 mb-4">
            <Input value={newTaskCodeName} onChange={(e)=>setNewTaskCodeName(e.target.value)} placeholder="Task code name" className="w-64"/>
            <Button type="submit">Add task code</Button>
          </form>
          <div className="rounded border overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Name</TableHead><TableHead className="text-right"></TableHead></TableRow></TableHeader>
              <TableBody>
                {taskCodes.map(t => (
                  <TableRow key={t.id}>
                    <TableCell><InlineText value={t.name} onChange={(v)=>saveTaskCode(t.id,v)} placeholder="Task code name"/></TableCell>
                    <TableCell className="text-right"><Button size="sm" variant="ghost" onClick={()=>removeTaskCode(t.id)}><Trash2 className="size-4"/></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function InlineText({ value, onChange, placeholder }){
  const [val, setVal] = React.useState(value || "");
  React.useEffect(()=>setVal(value || ""), [value]);
  return (
    <input
      value={val}
      onChange={(e)=>setVal(e.target.value)}
      onBlur={()=>onChange(val)}
      placeholder={placeholder}
      className="w-full bg-transparent outline-none border-b border-transparent focus:border-border transition-colors text-sm py-1"
    />
  );
}